/**
 * Live Cost Price numbers for tour copy.
 *
 * Read at render time from the same stores and helpers the page itself uses, so a card
 * can never state a figure the user cannot find on the screen behind it.
 *
 * Like the other values modules, each getter returns a `context` that i18next appends to
 * the description key. That is how a sentence only ever ships when its claim is true:
 * "your best seller is also your best earner" needs `getMostSoldItem` and
 * `getItemWithHighestMargin` to actually agree, and an account with nothing costed yet
 * falls back to the base key, which is the neutral wording carrying no numbers at all.
 */
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { useOrderHistory } from '../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../stores/appRevenueHistoryStore';
import {
  COST_PRICE_TYPE_CHOICE,
  calculateCostPercentage,
  calculateCostPriceKPIs,
  enrichCostPriceWithCurrentPrices,
  getEffectiveMenuPrice,
  getItemWithHighestMargin,
  getMostSoldItem,
} from '../../utils/costPriceUtils';
import { formatEuro, formatPct } from './tourValues';
import { getPracticeDataset } from './practice/practiceDataset';
import { PRACTICE_DISH_ID } from './practice/practiceGuards';

function readCostPriceSnapshot() {
  const practice = getPracticeDataset();
  const showExVat = useAppUserDefaultStore.getState().showExVatOrders;
  return {
    list: practice ? practice.costPriceList.map(dish => enrichCostPriceWithCurrentPrices(dish, practice.orderHistoryList, {
      showExVat, allSubrecipes: practice.subrecipesById,
    })) : useCostPriceStore.getState().costPriceList,
    revenue: practice?.revenueHistoryList ?? useRevenueHistory.getState().revenueHistoryList,
    revenueItems: practice?.revenueItems ?? [],
    showExVat: useAppUserDefaultStore.getState().showExVatOrders,
  };
}

/** The dish with the thinnest margin per plate, or null when nothing is costed and priced. */
function worstMarginDish() {
  const { list, revenue, showExVat } = readCostPriceSnapshot();
  let worst: { name: string; cost: number; price: number; margin: number; costPct: number } | null = null;

  for (const analysis of list) {
    if (analysis.id.startsWith('placeholder-')) continue;
    // CHOICE rows are per-pick surcharges, not dishes; they carry no menu price by design.
    if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) continue;
    if (!analysis.totalCost || analysis.totalCost <= 0) continue;
    const effective = getEffectiveMenuPrice(analysis, revenue);
    if (!effective) continue;
    const price = showExVat ? effective.menuExVatPrice : effective.menuPrice;
    if (!(price > 0)) continue;
    const margin = price - analysis.totalCost;
    if (!worst || margin < worst.margin) {
      worst = {
        name: analysis.costPriceName,
        cost: analysis.totalCost,
        price,
        margin,
        costPct: calculateCostPercentage(analysis.totalCost, price),
      };
    }
  }
  return worst;
}

/**
 * The KPI row. `context`: '' nothing costed yet · 'same' one dish leads both popularity
 * and margin · 'differ' two different dishes lead them.
 */
export function getCostPriceStandoutValues(): Record<string, string> {
  const { list, revenue, revenueItems, showExVat } = readCostPriceSnapshot();
  const kpis = calculateCostPriceKPIs(list, revenue, revenueItems, showExVat);
  const popular = getMostSoldItem(list, revenue, revenueItems);
  const highest = getItemWithHighestMargin(list, revenue, showExVat);
  if (!popular || !highest) return { context: '' };

  const shared = {
    dishCount: String(kpis.analyzedCount || 0),
    dishTotal: String(kpis.totalCount || 0),
    avgCostPct: formatPct(kpis.avgCostPrice),
    popularName: popular.itemName,
    popularUnits: String(popular.quantitySold),
    marginName: highest.itemName,
    marginAmount: formatEuro(highest.margin),
  };
  return { ...shared, context: popular.itemName === highest.itemName ? 'same' : 'differ' };
}

/**
 * The thin-margin dish. `context`: '' nothing measurable · 'loss' the dish costs at least
 * what it sells for · 'thin' it still earns, but least of all.
 */
export function getCostPriceLossValues(): Record<string, string> {
  const loss = worstMarginDish();
  if (!loss) return { context: '' };
  return {
    context: loss.margin <= 0 ? 'loss' : 'thin',
    lossName: loss.name,
    lossCost: formatEuro(loss.cost),
    lossPrice: formatEuro(loss.price),
    lossPct: formatPct(loss.costPct),
    lossMargin: formatEuro(Math.abs(loss.margin)),
  };
}

/**
 * The PRACTICE dish's own cost, recomputed live from the fixture invoice exactly the way
 * a real dish is. `context`: '' before anything has been added to it, 'costed' after.
 */
export function getPracticeDishValues(): Record<string, string> {
  const practice = getPracticeDataset();
  const dish = (practice?.costPriceList ?? useCostPriceStore.getState().costPriceList).find(analysis => analysis.id === PRACTICE_DISH_ID);
  if (!dish || dish.ingredients.length === 0) return { context: '' };
  const showExVat = useAppUserDefaultStore.getState().showExVatOrders;
  const enriched = enrichCostPriceWithCurrentPrices(dish, practice?.orderHistoryList ?? useOrderHistory.getState().orderHistoryList, { showExVat, allSubrecipes: practice?.subrecipesById });
  // Must be the same side of VAT the panel is showing, or the margin the card quotes
  // disagrees with the Menu Price tile right underneath it.
  const price = (showExVat ? dish.customMenuPriceExVat : dish.customMenuPrice) ?? 0;
  if (!(enriched.totalCost > 0) || !(price > 0)) return { context: '' };
  return {
    context: 'costed',
    dishCost: formatEuro(enriched.totalCost),
    dishPrice: formatEuro(price),
    dishMargin: formatEuro(price - enriched.totalCost),
    dishCostPct: formatPct(calculateCostPercentage(enriched.totalCost, price)),
  };
}

/** There is an already-created row to open. Guards `cost-open-dish`. */
export function hasOpenableDish(): boolean {
  return useCostPriceStore.getState().costPriceList.some(analysis => !analysis.id.startsWith('placeholder-'));
}
